import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FadeUp, Stagger, StaggerItem, EASE } from "@/components/Reveal";
import { waLink } from "@/data/site";

export const BRANCHES = [
  {
    title: "Psicoterapia",
    desc: "Estou procurando acompanhamento para questões emocionais, comportamentais ou relacionais.",
    chips: ["Crianças", "Adolescentes", "Adultos"],
    cta: "Conhecer Psicoterapia",
    to: "/psicoterapia",
    testId: "caminho-psicoterapia",
  },
  {
    title: "Avaliação Neuropsicológica",
    desc: "Quero compreender melhor aspectos do funcionamento cognitivo, emocional ou comportamental por meio de um processo avaliativo.",
    chips: ["Atenção", "Memória", "Aprendizagem", "Funções Executivas", "Desenvolvimento", "TDAH", "TEA", "Reavaliação"],
    cta: "Conhecer Neuropsicologia",
    to: "/neuropsicologia",
    testId: "caminho-neuropsicologia",
  },
  {
    title: "Ainda não sei",
    desc: "Tudo bem não chegar com essa resposta pronta. Você pode explicar brevemente sua demanda no primeiro contato e receber informações sobre os serviços disponíveis.",
    chips: [],
    cta: "Conversar com Sabrina",
    href: waLink(),
    testId: "caminho-ainda-nao-sei",
  },
];

const BranchCard = ({ b, i }) => (
  <FadeUp delay={0.45 + i * 0.18} className="h-full">
    <article
      data-testid={b.testId}
      className="group flex h-full flex-col rounded-3xl border border-raiz-900/10 bg-cream p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-raiz-600/50 lg:p-9"
    >
      <p className="font-serif text-3xl font-light text-raiz-300">0{i + 1}</p>
      <h3 className="mt-4 font-serif text-2xl font-light text-ink">{b.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-ink/65">{b.desc}</p>
      {b.chips.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {b.chips.map((c) => (
            <span key={c} className="rounded-full border border-raiz-800/15 bg-paper px-3.5 py-1.5 text-xs text-raiz-900">
              {c}
            </span>
          ))}
        </div>
      )}
      <div className="mt-7 flex-1" />
      {b.to ? (
        <Link
          to={b.to}
          data-testid={`${b.testId}-cta`}
          className="group/link inline-flex items-center gap-2 text-sm font-medium text-raiz-800"
        >
          {b.cta}
          <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1.5" />
        </Link>
      ) : (
        <a
          href={b.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${b.testId}-cta`}
          className="group/link inline-flex items-center gap-2 text-sm font-medium text-raiz-800"
        >
          {b.cta}
          <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1.5" />
        </a>
      )}
    </article>
  </FadeUp>
);

const RootMap = () => (
  <div data-testid="root-map">
    <div className="hidden lg:block">
      <FadeUp className="mx-auto max-w-md">
        <p
          data-testid="root-map-center"
          className="rounded-full border border-raiz-800/25 bg-raiz-800 px-8 py-5 text-center font-serif text-xl italic text-paper shadow-lg shadow-raiz-900/15"
        >
          O que você está buscando compreender?
        </p>
      </FadeUp>
      <svg viewBox="0 0 1200 180" className="mx-auto mt-2 w-full max-w-5xl" aria-hidden="true">
        {[
          "M600 0 C600 90, 220 60, 200 178",
          "M600 0 C600 90, 600 80, 600 178",
          "M600 0 C600 90, 980 60, 1000 178",
        ].map((d, i) => (
          <motion.path
            key={i}
            d={d}
            fill="none"
            stroke="rgba(75,93,52,0.4)"
            strokeWidth="1.5"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.5, delay: 0.15 + i * 0.12, ease: EASE }}
          />
        ))}
      </svg>
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3">
        {BRANCHES.map((b, i) => (
          <BranchCard key={b.title} b={b} i={i} />
        ))}
      </div>
    </div>

    <div className="lg:hidden">
      <FadeUp>
        <p
          data-testid="root-map-center-mobile"
          className="rounded-3xl border border-raiz-800/25 bg-raiz-800 px-6 py-5 text-center font-serif text-lg italic text-paper"
        >
          O que você está buscando compreender?
        </p>
      </FadeUp>
      <Stagger className="mt-8 space-y-6 border-l-2 border-raiz-600/30 pl-5" gap={0.14}>
        {BRANCHES.map((b, i) => (
          <StaggerItem key={b.title}>
            <article data-testid={`${b.testId}-mobile`} className="relative rounded-3xl border border-raiz-900/10 bg-cream p-6">
              <span aria-hidden="true" className="absolute -left-[26px] top-8 h-px w-5 bg-raiz-600/40" />
              <h3 className="font-serif text-xl text-ink">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/65">{b.desc}</p>
              {b.chips.length > 0 && (
                <ul className="mt-4 space-y-1.5 text-sm text-raiz-800">
                  {b.chips.map((c, j) => (
                    <li key={c} className="flex items-center gap-2">
                      <span aria-hidden="true" className="text-raiz-400">{j === b.chips.length - 1 ? "└" : "├"}</span>
                      {c}
                    </li>
                  ))}
                </ul>
              )}
              {b.to ? (
                <Link to={b.to} data-testid={`${b.testId}-cta-mobile`} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-raiz-800">
                  {b.cta} <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
              ) : (
                <a href={b.href} target="_blank" rel="noopener noreferrer" data-testid={`${b.testId}-cta-mobile`} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-raiz-800">
                  {b.cta} <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </a>
              )}
            </article>
          </StaggerItem>
        ))}
      </Stagger>
    </div>

    <FadeUp delay={0.3} className="mt-12 text-center">
      <p className="mx-auto max-w-xl text-xs leading-relaxed text-ink/45">
        Este mapa é apenas uma orientação sobre os serviços disponíveis — ele não fornece diagnóstico. Cada demanda é compreendida individualmente no primeiro contato.
      </p>
    </FadeUp>
  </div>
);

export default RootMap;
